import type { CampaignDetails, SelectedTag } from '~publish/legacy/tags/types'
import type { LinkedInAnnotationEntity } from '~publish/legacy/editor/plugins/linkedin-annotations/nodes'

import type { Document } from '../composer/composer/entities/Document'
import type { ChannelData } from '../composer/composer/entities/Draft'
import type {
  MediaSource,
  RetweetMedia,
} from '../composer/composer/entities/factories'
import type { UserTag } from '../composer/composer/stores/types'
import type { FacebookMentionDbEntry } from '../editor/plugins/facebook-mention/nodes'
import type { PublishingNote } from './graphqlTypes'

export interface ParsedWebAPIUtilsRetweetData {
  user_id: number | string
  tweet_id: string
  user_name: string
  display_name: string
  url: string
  avatar_http: string
  avatar_https: string
  comment: string
}

export interface RpcUpdateRetweetData {
  avatars: {
    http: string
    https: string
  }
  profile_name: string
  text: string
  tweet_id: string | number
  url: string
  user_id: string | number
  username: string
  comment?: string
  comment_formatted?: string
  created_at?: number
  media?: RetweetMedia[]
}

export enum PostEvents {
  POST_ADDED = 'added_update',
  POST_DELETED = 'deleted_update',
  POST_REORDERED = 'reordered_updates',
  POST_SENT = 'sent_update',
  POST_UPDATED = 'updated_update',
  POST_NOTE_ADDED = 'added_post_note',
  POST_NOTE_DELETED = 'deleted_post_note',
  POST_NOTE_UPDATED = 'updated_post_note',
}

export type RpcUpdateStatistics = {
  retweets?: number
  repins?: number
  favorites?: number
  mentions?: number
  clicks?: number
  reach?: number
}

export type RpcVideoDetails = {
  audio_codec?: string | null
  duration: number
  duration_millis: number
  file_size: number
  width: number
  height: number
  transcoded_location: string
  location: string
}

export type RpcMediaVideo = {
  id: string
  details: RpcVideoDetails
  thumbnails?: string[]
  title: string
  thumb_offset?: number | string
}

export type RpcUpdateImage = {
  progress: string
  uploaded: string
  thumbnail: string
  alt_text?: string | null
  source?: MediaSource
  width: string
  height: string
  picture: string
  photo?: string
  photo_dimensions?: { w: number; h: number }
}

export type RpcUpdateVideo = {
  progress: string
  uploaded: string
  thumbnail: string
  uploading_type: 'video'
  alt_text?: string
  source?: string
  video: RpcMediaVideo
}

export type RpcUpdateDocument = {
  progress: string
  uploaded: string
  thumbnail: string
  uploading_type: 'document'
  document: {
    id: string
    title: Document['title']
    details: Pick<Document, 'pageCount' | 'fileSizeBytes'> & {
      location: Document['url']
    }
  }
}

export type RpcUpdateLinkMedia = {
  description: string
  link: string
  original_preview: string
  expanded_link?: string
  preview: string
  thumbnail: string
  title: string
}

export type RpcUpdateMedia =
  | RpcUpdateImage
  | RpcUpdateVideo
  | RpcUpdateDocument
  | RpcUpdateLinkMedia

export type RpcPostContent = {
  imageSrc?: string
  imageUrls?: string[]
  linkAttachment?: { title: string }
  links?: string[]
  retweetCommentLinks?: string[]
  text: string
  type: string
}

export type RpcPostDetails = {
  postAction: string
  isRetweet: boolean
  error?: unknown
  errorLink?: string
  isCustomScheduled: boolean
  isInstagramReminder: boolean
  isPendingTiktokPost: boolean
}

export type RpcUpdateUser = {
  email: string
  name?: string
  gravatar?: string
  avatar: string
  id: string
}

export enum UpdateStatus {
  Buffer = 'buffer',
  Pending = 'pending',
  PendingTiktok = 'pending_tiktok',
  Draft = 'draft',
  Sent = 'sent',
  Notified = 'notified',
  Error = 'error',
  Analytics = 'analytics',
  Processing = 'processing',
  ProcessingAnalytics = 'processing_analytics',
  Service = 'service',
  ServiceReply = 'service_reply',
  Library = 'library',
  PendingApproval = 'pending_approval',
}

export type RpcUpdateCommonContent = {
  text: string
  media?: RpcUpdateMedia
  extra_media?: RpcUpdateMedia[]
  links: string[] | []
  retweet?: RpcUpdateRetweetData
  retweeted_tweet_id?: string
  retweetCommentLinks?: string[]
}

export type RpcUpdate = RpcUpdateCommonContent & {
  day: string
  id: string
  status: UpdateStatus | ''
  channel_data?: ChannelData
  channelData?: ChannelData
  updateType?: string
  createdAt: number
  profileId: string
  isPastDue: boolean
  imageSrc?: string
  imageUrls: string[] | []
  scheduled_at?: number
  scheduledAt?: number
  profileTimezone: string
  linkAttachment: { title?: string }
  needsApproval: boolean
  postDetails: RpcPostDetails
  profile_service: string
  isSent: boolean
  isPastReminder: boolean
  isReminder: boolean
  isDraft: boolean
  title?: string
  source_url?: string
  type: string
  subprofile_id?: string
  due_at: number
  dueTime?: string
  pinned: boolean
  isFixed: boolean
  statistics: RpcUpdateStatistics
  user: RpcUpdateUser
  serviceLink?: string
  serviceUpdateId?: string
  campaignDetails?: CampaignDetails
  postContent: RpcPostContent
  hasPushNotifications?: boolean
  profileService?: string
  profileServiceType?: string
  dueAt?: number
  retweet?: RpcUpdateRetweetData
  retweeted_tweet_id?: string
  retweetCommentLinks?: string[]
  entities?: FacebookMentionDbEntry[]
  annotations?: LinkedInAnnotationEntity[]
  notes?: PublishingNote[]
  headerDetails?: {
    channel?: {
      avatarUrl: string
      handle: string
      type: string
    }
    hideCreatorDetails?: boolean
  }
  ideaId?: string
  fromIdea?: boolean
  thread?: RpcUpdateCommonContent[]
  via?: string
  tags: SelectedTag[]
  view?: string
  service_user_tags?: UserTag[]
}

export enum PostActionType {
  RESCHEDULE = 'RESCHEDULE',
  ADD_TO_QUEUE = 'ADD_TO_QUEUE',
  REQUEUE = 'REQUEUE',
  SCHEDULE_POST = 'SCHEDULE_POST',
  REQUEST_APPROVAL = 'REQUEST_APPROVAL',
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  REVERT_TO_DRAFT = 'REVERT_TO_DRAFT',
  MOVE_POST_TO_DRAFTS = 'MOVE_POST_TO_DRAFTS',
  SHARE_NOW = 'SHARE_NOW',
  SHARE_LINK = 'SHARE_LINK',
  SHARE_AGAIN = 'SHARE_AGAIN',
  VIEW_POST = 'VIEW_POST',
}

export enum PostActionButtonType {
  // Post action prefers to be rendered separately
  SINGLE = 'SINGLE',
  // Post action prefers to be rendered nested along with other post actions
  STACKED = 'STACKED',
}

export enum PostActionLabel {
  RESCHEDULE = 'Reschedule',
  ADD_TO_QUEUE = 'Add to Queue',
  REQUEUE = 'Re-add to Queue',
  SCHEDULE_POST = 'Schedule Post',
  REQUEST_APPROVAL = 'Request Approval',
  APPROVE = 'Approve',
  REJECT = 'Reject',
  REVERT_TO_DRAFT = 'Revert to Draft',
  MOVE_POST_TO_DRAFTS = 'Move to Drafts',
  SHARE_NOW = 'Share Now',
  RETRY_NOW = 'Retry Now',
  SHARE_LINK = 'Share Link',
  SHARE_AGAIN = 'Share Again',
  VIEW_POST = 'View Post',
}

export const PostActionTooltipMessage = Object.freeze({
  RESCHEDULE: '',
  ADD_TO_QUEUE:
    'This draft will be added to the next available time slot in the queue.',
  REQUEUE: 'This post will be re-added to the top of the queue.',
  SCHEDULE_POST:
    'This draft will be added to the queue for the selected date and time.',
  REQUEST_APPROVAL: '',
  APPROVE: '',
  REJECT: 'This post will be moved back to drafts.',
  REVERT_TO_DRAFT: '',
  MOVE_POST_TO_DRAFTS: 'This post will be turned into a draft.',
  SHARE_NOW: 'This post will be shared to your channel immediately',
  SHARE_LINK: 'Schedule a post containing a link to this post.',
  SHARE_AGAIN: 'Schedule this post to be shared again.',
  VIEW_POST: 'View this post on the network it was shared to.',
})

export type PostActionTooltipMessage =
  (typeof PostActionTooltipMessage)[keyof typeof PostActionTooltipMessage]
